import React from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import SPADialog from '../../../shared/SPADialog';
import { checkLabel } from '../reportParams';
import Eyebrow from './Eyebrow';
import Provenance from './Provenance';
import ReasonCallout from './ReasonCallout';
import ScreenedQuestion from './ScreenedQuestion';
import { contextLine, screenedAt } from './failureContext';
import type { FailureDetail } from '../types';

interface FailureDialogProps {
  failureId: string | null;
  onClose: () => void;
}

// AIDEV-NOTE: The only place the screened question text is shown — it is kept out of the CSV on
// purpose (Art. IV § 8 asks for a summary of findings, not the question bank). Opened by ?failure=<id>
// so a row deep-links from both the SPA and the legacy mount without a nested Rails route.
const FailureDialog = ({ failureId, onClose }: FailureDialogProps) => {
  const { data, error } = useQuery({
    enabled: failureId !== null,
    queryFn: () => axios
      .get<FailureDetail>(Routes.admin_ai_safety_report_path(failureId as string, { format: 'json' }))
      .then(res => res.data),
    queryKey: [QueryKeys.AISafetyFailure, failureId],
  });

  return (
    <SPADialog open={failureId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>{data ? `${checkLabel(data.checkName)} failure` : 'Screening result'}</SPADialog.Title>
          <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
            {data ? (
              <>
                <span className="tw-block">{contextLine(data)}</span>
                <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-spa-tertiary">
                  {`Screened ${screenedAt(data.createdAt)}`}
                  {data.modelIdentifier && ` by ${data.modelIdentifier}`}
                </span>
              </>
            ) : 'Loading the screened row.'}
          </SPADialog.Description>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body>
          {error && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error">Couldn’t load this screening result.</p>
          )}
          {data && (
            <div className="tw-flex tw-flex-col tw-gap-5">
              <ReasonCallout reason={data.reason} />

              <section>
                <Eyebrow tag="h3">Screened question</Eyebrow>
                <ScreenedQuestion content={data.subjectContent} />
              </section>

              <Provenance detail={data} />
            </div>
          )}
        </SPADialog.Body>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default FailureDialog;
